import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import Container from 'component/atom/container/Container';
import SettingsHeader from 'pages/authenticated/layout/settingsLayout/settingsHeader/SettingsHeader';
import styles from './BotSettings.module.scss';
import { useFetch } from 'hooks/useFetch';
import { usePost } from 'hooks/usePost';
import { scriptEndpoints } from 'hooks/endpoints';
import Loader from 'component/atom/loader/Loader';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Input from 'component/atom/forms/input/input/Input';
import NoData from 'component/molecules/Table/TableComponents/NoData/NoData';
import { isEmpty } from 'lodash';

const Form = ({ formik, isLoading, urls, setUrls, setTypedUrl }) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      formik.handleSubmit();
    }
  };

  const handleRemove = (index) => {
    setUrls(urls.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    formik.setFieldValue('url', e.target.value);
    setTypedUrl(e.target.value);
  };

  return (
    <form
      autoComplete='off'
      onSubmit={formik.handleSubmit}
      className={styles.formCont}
    >
      <Input
        label='Add Url'
        placeholder='Enter Url'
        name='url'
        value={formik.values.url}
        onChange={(value) => handleChange(value)}
        onBlur={formik.handleBlur}
        error={formik.errors.url}
        touched={formik.touched.url}
        onKeyDown={handleKeyDown}
        inputClassname={styles.formInput}
      />
      <div className={styles['cont']}>
        {urls?.length > 0 && <p>Website</p>}
        {isLoading ? (
          <Loader isComponent />
        ) : urls?.length > 0 ? (
          urls?.map((url, index) => (
            <div key={index} className={styles['single-web']}>
              <div className={styles['attachment']}>
                <SvgWithBranding type='websiteLink' className={styles.icon} />
                <p>{url}</p>
              </div>
              <SvgWithBranding
                type='closeSvg'
                className={styles.icon}
                color='#C81111'
                onClick={() => handleRemove(index)}
              />
            </div>
          ))
        ) : (
          <div className={styles['no-data-container']}>
            <NoData />
          </div>
        )}
      </div>
    </form>
  );
};

const BotSettings = () => {
  const { id } = useParams();
  const [urls, setUrls] = useState([]);
  const [typedUrl, setTypedUrl] = useState('');

  const validationSchema = Yup.object({
    url: Yup.string()
      .trim()
      .url('Enter a valid URL (must include http:// or https://)'),
  });

  const formik = useFormik({
    initialValues: {
      url: '',
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      setUrls((prev) => [...prev, values.url]);
      formik.resetForm();
    },
    enableReinitialize: true,
  });

  const {
    data: restrictedURL,
    refetch: refetchURL,
    isLoading,
  } = useFetch(
    ['restrictedUrlByBot', id],
    scriptEndpoints.getRestrictedUrl(id),
    {
      enabled: !!id,
    }
  );

  const { mutate: AddURL, isPending } = usePost({
    onSuccessToast: 'Updated Successfully',
    toastType: 'add',
  });

  useEffect(() => {
    setUrls(restrictedURL?.data);
  }, [restrictedURL]);

  const handleUpdate = () => {
    if (formik.errors.url || (isEmpty(restrictedURL?.data) && isEmpty(urls)))
      return;
    const trimmed = formik.values.url?.trim();
    let newUrls = [...urls];

    if (trimmed && !urls.includes(trimmed)) {
      newUrls.push(trimmed);
    }

    AddURL(
      {
        url: scriptEndpoints.updateRestrictedUrl(),
        method: 'Put',
        data: {
          botId: id,
          url: newUrls,
        },
      },
      {
        onSuccess: () => {
          refetchURL();
          formik.resetForm();
        },
      }
    );
  };

  const rightButtons = [
    {
      label: 'Save',
      type: 'save',
      func: () => handleUpdate(),
      isLoading: isPending,
    },
  ];

  return (
    <div className={`${styles.cont} bot-setting-container`}>
      <SettingsHeader title='Restrictions' rightButtons={rightButtons} />
      <Container className={styles['container']}>
        <Form
          formik={formik}
          isLoading={isLoading}
          handleUpdate={handleUpdate}
          urls={urls}
          setUrls={setUrls}
          setTypedUrl={setTypedUrl}
        />
      </Container>
    </div>
  );
};

export default BotSettings;
